import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Shield, Plus, Edit2, Trash2, Search, FileText, CheckCircle, XCircle, Save } from 'lucide-react';
import { globalPromptsAPI } from '../services/api';
import { theme } from '../styles/theme';
import { Chip } from '../components/Chip';
import { MetricTile, MetricTileGrid } from '../components/MetricTile';
import { extractApiError } from '../utils/errorMessages';
import { useToast } from '../hooks/use-toast';
import { useConfirm } from '../contexts/ConfirmContext';

interface GlobalTemplate {
  id: number;
  name: string;
  category: string;
  description: string;
  template_text: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const SystemSettings: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();

  // Redirect non-admin users
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  const [templates, setTemplates] = useState<GlobalTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<GlobalTemplate | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    template_text: '',
    is_active: true,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await globalPromptsAPI.list();
      const templatesData: any = response.data;
      const allTemplates = templatesData.results || templatesData;
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast({ title: extractApiError(error, "Couldn't load templates"), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await globalPromptsAPI.create(formData);
      toast({ title: `${formData.name} created`, variant: 'success' });
      setShowCreateModal(false);
      resetForm();
      loadTemplates();
    } catch (error: any) {
      const errorMsg = error.response?.data?.name?.[0] || 'Failed to create template';
      toast({ title: errorMsg, variant: 'error' });
    }
  };

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    
    try {
      await globalPromptsAPI.update(String(selectedTemplate.id), formData);
      toast({ title: `${formData.name} saved`, variant: 'success' });
      setShowEditModal(false);
      resetForm();
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't save that template"), variant: 'error' });
    }
  };

  const handleDeleteTemplate = async (id: number, name: string) => {
    const ok = await confirm({
      title: `Delete ${name}?`,
      description: 'This cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      await globalPromptsAPI.delete(String(id));
      toast({ title: `${name} deleted`, variant: 'success' });
      loadTemplates();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't delete that template"), variant: 'error' });
    }
  };

  const handleToggleActive = async (template: GlobalTemplate) => {
    try {
      await globalPromptsAPI.update(String(template.id), { is_active: !template.is_active });
      loadTemplates();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't change that template"), variant: 'error' });
    }
  };

  const openEditModal = (template: GlobalTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      description: template.description,
      template_text: template.template_text,
      is_active: template.is_active,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      template_text: '',
      is_active: true,
    });
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchQuery === '' || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(templates.map(t => t.category)));

  const stats = {
    total: templates.length,
    active: templates.filter(t => t.is_active).length,
    inactive: templates.filter(t => !t.is_active).length,
    categories: categories.length,
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <Link 
        to="/admin" 
        className={`inline-flex items-center ${theme.text.secondary} hover:text-interactive-primary-hover mb-4 transition`}
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Admin Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="sr-only">System Settings</h2>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create Template</span>
        </button>
      </div>

      {/* Stats */}
      <MetricTileGrid columns={4} className="mb-6">
        <MetricTile label="Total Templates" value={stats.total} IconComponent={FileText} />
        <MetricTile label="Active" value={stats.active} IconComponent={CheckCircle} />
        <MetricTile label="Inactive" value={stats.inactive} IconComponent={XCircle} />
        <MetricTile label="Categories" value={stats.categories} IconComponent={Shield} />
</MetricTileGrid>

      {/* Filters */}
      <div className={`${theme.card.background} rounded-lg shadow p-6 mb-6`}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary w-5 h-5" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Templates Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-border"></div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className={`${theme.card.background} rounded-lg shadow p-12 text-center`}>
          <FileText className="w-16 h-16 mx-auto text-text-tertiary mb-4" />
          <p className={`${theme.text.secondary} text-lg`}>No templates found</p>
        </div>
      ) : (
        <div className={`${theme.card.background} rounded-lg shadow overflow-hidden`}>
          <table className={`min-w-full divide-y ${theme.border.primary.replace('border-', 'divide-')}`}>
            <thead className={`${theme.background.primary}`}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`${theme.card.background} divide-y divide-border`}>
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-bg-subtle">
                  <td className="px-6 py-4">
                    <div>
                      <div className={`text-sm font-medium ${theme.text.primary}`}>
                        {template.name}
                      </div>
                      <div className="text-xs text-text-tertiary mt-1">
                        {template.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Chip>{template.category}</Chip>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {template.is_active ? (
                      <Chip icon={<CheckCircle className="w-3 h-3" aria-hidden="true" />}>Active</Chip>
                    ) : (
                      <Chip tone="muted" icon={<XCircle className="w-3 h-3" aria-hidden="true" />}>Inactive</Chip>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(template)}
                        className="p-2 text-text-secondary hover:text-qc-success"
                        title="Toggle Active Status"
                      >
                        {template.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => openEditModal(template)}
                        className="p-2 text-text-secondary hover:text-interactive-primary-hover"
                        title="Edit Template"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id, template.name)}
                        className="p-2 text-text-secondary hover:text-qc-error"
                        title="Delete Template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
              {showCreateModal ? <Plus className="w-6 h-6 mr-2" /> : <Edit2 className="w-6 h-6 mr-2" />}
              {showCreateModal ? 'Create Template' : 'Edit Template'}
            </h2>
            <form onSubmit={showCreateModal ? handleCreateTemplate : handleUpdateTemplate}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Template Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Category *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Data Cleansing, Formatting, etc."
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Description *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Template Text *
                  </label>
                  <textarea
                    required
                    rows={8}
                    value={formData.template_text}
                    onChange={(e) => setFormData({ ...formData, template_text: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} font-mono text-sm`}
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-text-secondary border-border rounded focus:ring-border-strong"
                  />
                  <label htmlFor="is_active" className={`ml-2 text-sm ${theme.text.secondary}`}>
                    Active (users can see this template)
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    showCreateModal ? setShowCreateModal(false) : setShowEditModal(false);
                    resetForm();
                    setSelectedTemplate(null);
                  }}
                  className={`px-4 py-2 ${theme.text.secondary} hover:bg-bg-subtle rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{showCreateModal ? 'Create' : 'Update'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
